import React, { useState, useMemo} from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { DateTimePicker } from '@material-ui/pickers';
import { Duration } from 'luxon';
import { Autocomplete, LoadScript } from '@react-google-maps/api';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import Collapse from '@material-ui/core/Collapse';

import States from 'app/utils/States';
import { useEffect } from 'react';

const libs = ['places'];

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
        required
        margin="normal"
    />
);

const useStyles = makeStyles(theme => ({
    section: {
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    mobileContainer: {
        height: '100%',
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginLeft: {
        marginLeft: theme.spacing(1)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginBottomLarge: {
        marginBottom: theme.spacing(6)
    },
    image: {
        maxHeight: 300,
        maxWidth: 300,
        height: 'auto',
        width: '100%'
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: theme.spacing(6)
    },
    height: {
        height: '100%'
    },
    opacity: {
        opacity: 0.5
    }
}));

const eventPeriods = [
    'days',
    'weeks',
    'months',
    'years'
];

const getDateFormat = (event) => {
    switch (event.repeat_period) {
        case 'years':
            return 'y';
        case 'months':
            return 'LLLL y';
        case 'weeks':
        case 'days':
            return 'DDD';
        default: return '';
    }
};


const getAddressFromComponents = ac => {
    const streetNumber = ac.find( g => g.types.find( t => t === 'street_number') ).long_name;
    const streetName = ac.find( g => g.types.find( t => t === 'route' )).long_name;
    const cityName = ac.find( g => g.types.find( t => t === 'locality') && g.types.find( t => t === 'political' )).long_name;
    const stateName = ac.find( g => g.types.find( t => t === 'administrative_area_level_1') && g.types.find( t => t === 'political' )).short_name;
    const countryName = ac.find( g => g.types.find( t => t === 'country') && g.types.find( t => t === 'political' )).short_name;
    const zip = ac.find( g => g.types.find( t => t === 'postal_code' )).long_name;

    return {
        address: `${streetNumber} ${streetName}`,
        city: cityName,
        state: stateName,
        country: countryName,
        zip: zip
    };
}

export default function General(props) {

    const {
        event,
        handleEvent,
        handleLocation,
        isError,
        getErrorMessage,
        newEvent
    } = props;

    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ autoComplete, setAutoComplete ] = useState(null);

    useEffect(() => {
        if (autoComplete !== null) {
            autoComplete.setFields([
                'name',
                'address_components',
                'formatted_address'
            ])
        }
    }, [autoComplete]);

    const handleAutoCompleteLoaded = ac => setAutoComplete(ac);

    const handleAutoComplete = () => {
        if (autoComplete !== null) {
            const p = autoComplete.getPlace();

            console.log(p);

            if (p.address_components) {
                const geocoder = new window.google.maps.Geocoder();
                let ac = getAddressFromComponents(p.address_components);
                ac.name = p.name;
                handleLocation(ac);
                geocoder.geocode({ address: p.formatted_address })
                    .then(({ results }) => {
                        console.log(results[0]);
                    });
            } else {
                handleEvent('location', p.name);
            }
        }
    }

    const eventNameWithDate = useMemo(() => {
        if (event.repeated) {

            const dateFormat = getDateFormat(event);

            switch (event.add_date_to_name) {
                case 0: return event.name;
                case 1:
                    return event.starts_at.toFormat(dateFormat) + ' ' + event.name;
                case 2:
                    return event.name + ' ' + event.starts_at.toFormat(dateFormat);
            }
        } else {
            return event.name;
        }

    }, [event.add_date_to_name, event.repeated, event.name, event.starts_at, event.repeat_period]);

    const eventDuration = useMemo(() => {

        let d = event.ends_at.diff(event.starts_at, ["years", "months", "days", "hours", 'minutes']);
        let dString = '';

        d = d.toObject();

        if (d.years > 0) dString += d.years === 1 ? '1 year ' : `${d.years} years `;
        if (d.months > 0) dString += d.months === 1 ? '1 month ' : `${d.months} month `;
        if (d.days > 0) dString += d.days === 1 ? '1 day ' : `${d.days} days `;
        if (d.hours > 0) dString += d.hours === 1 ? '1 hour ' : `${d.hours} hours `;
        if (d.minutes > 0) dString += d.minutes === 1 ? '1 minute ' : `${d.minutes} minutes`;

        return dString;
    }, [event.starts_at, event.ends_at]);


    return (
        <Grid container direction="row" className={newEvent ? classes.height : null}>
            {!newEvent ? null : isMobile ? null :
                <Grid item xs className={classes.height}>
                    <div className={classes.imageContainer}>
                        <img className={classes.image} src='/images/step-election.png' alt="event-graphic" />
                    </div>
                </Grid>
            }
            <Grid item xs={isMobile ? 12 : newEvent ? 7 : 12} className={isMobile ? classes.mobileContainer : newEvent ? classes.formContainer : null}>
                {newEvent ? (
                    <Typography variant="h6" gutterBottom>
                        New Event
                    </Typography>
                ) : (
                    <Typography variant="h6" gutterBottom>
                        General Settings
                    </Typography>
                )}
                <TextField
                    id="input-name"
                    label="Title"
                    value={event.name}
                    margin="normal"
                    onChange={e => handleEvent('name', e)}
                    fullWidth
                    autoFocus
                    error={isError('name')}
                    helperText={getErrorMessage('name')}
                    variant="outlined"
                    required
                />
                <TextField
                    id="short-description"
                    label="Short Description"
                    value={event.short_description}
                    onChange={e => handleEvent('short_description', e)}
                    fullWidth
                    error={isError('short_description')}
                    helperText="This should be a brief description used to quickly summarize the event. Will be displayed on the events page and home page."
                    variant="outlined"
                    multiline
                    margin="normal"
                    rows="4"
                    rowsMax="4"
                    inputProps={{
                        maxLength: "191"
                    }}
                    required
                />
                <DateTimePicker
                    label="Start Time"
                    inputVariant="outlined"
                    value={event.starts_at}
                    onChange={e => handleEvent('starts_at', e)}
                    variant="outlined"
                    TextFieldComponent={renderDateInput}
                    minutesStep={15}
                />
                <DateTimePicker
                    label="End Time"
                    inputVariant="outlined"
                    value={event.ends_at}
                    onChange={e => handleEvent('ends_at', e)}
                    variant="outlined"
                    TextFieldComponent={renderDateInput}
                    minutesStep={15}
                />
                <Typography variant="subtitle2" color="textSecondary" className={classes.marginBottom}>
                    Event Duration: {eventDuration}
                </Typography>
                <Typography variant="subtitle1" color="primary" className={classes.marginBottom}>
                    Event Location
                </Typography>
                <Grid container spacing={3} className={classes.marginBottom}>
                    <Grid item xs={12}>
                        <LoadScript googleMapsApiKey='AIzaSyDN1_PZDAq2mCnTFGc279pXo-MIIdqugME' libraries={libs}>
                            <Autocomplete
                                onLoad={handleAutoCompleteLoaded}
                                onPlaceChanged={handleAutoComplete}
                                >
                                <TextField
                                    id="location-search"
                                    label="Search for a Location"
                                    fullWidth
                                    variant="outlined"
                                />
                            </Autocomplete>
                        </LoadScript>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            id="input-location"
                            label="Location"
                            value={event.location ?? ""}
                            onChange={e => handleEvent('location', e)}
                            fullWidth
                            variant='outlined'
                            error={isError('location')}
                            helperText={getErrorMessage('location')}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            id="input-address"
                            label="Address"
                            value={event.address ?? ""}
                            onChange={e => handleEvent('address', e)}
                            fullWidth
                            variant='outlined'
                            error={isError('address')}
                            helperText={getErrorMessage('address')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={5} md={12} lg={6}>
                        <TextField
                            id="input-city"
                            label="City"
                            value={event.city ?? ""}
                            onChange={e => handleEvent('city', e)}
                            fullWidth
                            variant='outlined'
                            error={isError('city')}
                            helperText={getErrorMessage('city')}
                            />
                    </Grid>
                    <Grid item xs={12} sm={4} md={12} lg={2}>
                        <TextField
                            id="input-state"
                            select
                            label="State"
                            value={event.state ?? "AL"}
                            onChange={e => handleEvent('state', e)}
                            fullWidth
                            variant='outlined'
                            error={isError('state')}
                            helperText={getErrorMessage('state')}
                            >
                            {States.map( state => {
                                return (
                                    <MenuItem
                                        key={state.abbreviation}
                                        value={state.abbreviation}
                                        >
                                        {state.name}
                                    </MenuItem>
                                )
                            })}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3} md={12} lg={4}>
                        <TextField
                            id="input-zip"
                            label="ZIP"
                            value={event.zip ?? ""}
                            onChange={e => handleEvent('zip', e)}
                            fullWidth
                            variant='outlined'
                            error={isError('zip')}
                            helperText={getErrorMessage('zip')}
                            />
                    </Grid>
                </Grid>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                    Repeat Event
                </Typography>
                <Tooltip title="Repeating Events are currently not available. Please try again later.">
                    <FormControlLabel
                        className={classes.marginBottom}
                        control={
                            <Switch
                                checked={event.repeated}
                                onChange={e => handleEvent('repeated', e)}
                                color="primary"
                                value="repeated"
                                disabled={true}
                            />
                        }
                        label="Would you like this event to repeat?"
                        labelPlacement="end"
                        margin="normal"
                    />
                </Tooltip>
                <Collapse in={event.repeated}>
                    <div className={classes.section}>
                        <Typography variant='subtitle2' gutterBottom>
                            How often should the event repeat?
                        </Typography>
                        <Grid container spacing={3} alignItems="center" wrap="nowrap">
                            <Grid item>
                                <Typography variant='subtitle2' color="textSecondary" align="right">
                                    Every
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    id="input-repeat-amount"
                                    value={event.repeat_amount}
                                    onChange={e => handleEvent('repeat_amount', e)}
                                    fullWidth
                                    type="number"
                                    error={isError('repeat_amount')}
                                    helperText={getErrorMessage('repeat_amount')}
                                    variant="outlined"
                                    disabled={! event.repeated}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    id="input-repeat-period"
                                    select
                                    value={event.repeat_period}
                                    onChange={e => handleEvent('repeat_period', e)}
                                    variant="outlined"
                                    fullWidth
                                    error={isError('repeat_period')}
                                    helperText={getErrorMessage('repeat_period')}
                                    disabled={! event.repeated}
                                    >
                                    <MenuItem value="">
                                        Select a period
                                    </MenuItem>
                                    {eventPeriods.map((period, i) => (
                                        <MenuItem key={i} value={period}>
                                            {period}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.section}>
                        <Typography variant='subtitle2' gutterBottom>
                            Limit to a certain number of repeats?  (Leave '0' to repeat indefinitely.)
                        </Typography>
                        <TextField
                            id="repeat-cycles"
                            variant="outlined"
                            value={event.repeat_cycles}
                            onChange={e => handleEvent('repeat_cycles', e)}
                            fullWidth
                            error={isError('repeat_cycles')}
                            type='number'
                            InputProps={{
                                inputProps: {
                                    min: 0,
                                    step: 1
                                }
                            }}
                            disabled={! event.repeated}
                        />
                    </div>
                    <div className={classes.section}>
                        <Typography variant='subtitle2' gutterBottom>
                            Would you like to update the event name with the date every cycle?
                        </Typography>
                        <TextField
                            id="input-add-date-to-name"
                            select
                            value={event.add_date_to_name}
                            onChange={e => handleEvent('add_date_to_name', e)}
                            variant="outlined"
                            fullWidth
                            error={isError('add_date_to_name')}
                            helperText={getErrorMessage('add_date_to_name')}
                            disabled={! event.repeated}
                            >
                            <MenuItem value={0}>
                                Do Not Update
                            </MenuItem>
                            <MenuItem value={1}>
                                Add to Beginning
                            </MenuItem>
                            <MenuItem value={2}>
                                Add to End
                            </MenuItem>
                        </TextField>
                        <Typography variant='subtitle2' display="inline" color="textSecondary">
                            The name will display as:&nbsp;&nbsp;&nbsp;
                        </Typography>
                        <Typography variant='subtitle2' display="inline" color="primary">
                            {eventNameWithDate}
                        </Typography>
                    </div>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Grid container direction={isMobile ? "column" : "row"} justify="space-between" wrap="nowrap">
                                <Grid item xs={isMobile ? 12 : 6} style={{marginRight: isMobile ? 0 : 16}} >
                                    <Typography variant='subtitle2' gutterBottom>
                                        How many days in advance can guests see a repeat event?
                                    </Typography>
                                    <TextField
                                        id="advanced_days_viewable"
                                        variant="outlined"
                                        value={event.advanced_days_viewable}
                                        onChange={e => handleEvent('advanced_days_viewable', e)}
                                        fullWidth
                                        error={isError('advanced_days_viewable')}
                                        type='number'
                                        InputProps={{
                                            inputProps: {
                                                min: 1,
                                                step: 1
                                            }
                                        }}
                                        disabled={! event.repeated}
                                    />
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6} className={isMobile ? classes.marginTop : null}>
                                    <Typography variant='subtitle2' gutterBottom>
                                        How many days in advance can guests RSVP to a repeated event?
                                    </Typography>
                                    <TextField
                                        id="advanced_days_rsvpable"
                                        variant="outlined"
                                        value={event.advanced_days_rsvpable}
                                        onChange={e => handleEvent('advanced_days_rsvpable', e)}
                                        fullWidth
                                        error={isError('advanced_days_rsvpable')}
                                        type='number'
                                        InputProps={{
                                            inputProps: {
                                                min: 1,
                                                step: 1
                                            }
                                        }}
                                        disabled={! event.repeated}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction={isMobile ? "column" : "row"} justify="space-between" wrap="nowrap">
                                <Grid item xs={isMobile ? 12 : 6} style={{marginRight: isMobile ? 0 : 16}} >
                                    <Typography variant='subtitle2' gutterBottom>
                                        How many days in advance can guests purchase tickets to a repeat event?
                                    </Typography>
                                    <TextField
                                        id="advanced_days_purchasable"
                                        variant="outlined"
                                        value={event.advanced_days_purchasable}
                                        onChange={e => handleEvent('advanced_days_purchasable', e)}
                                        fullWidth
                                        error={isError('advanced_days_purchasable')}
                                        type='number'
                                        InputProps={{
                                            inputProps: {
                                                min: 1,
                                                step: 1
                                            }
                                        }}
                                        disabled={! event.repeated}
                                    />
                                </Grid>
                                <Grid item xs={isMobile ? 12 : 6} className={isMobile ? classes.marginTop : null}>
                                    <Typography variant='subtitle2' gutterBottom>
                                        How many days in advance can guests send invites to a repeated event?
                                    </Typography>
                                    <TextField
                                        id="advanced_days_invitable	"
                                        variant="outlined"
                                        value={event.advanced_days_invitable	}
                                        onChange={e => handleEvent('advanced_days_invitable', e)}
                                        fullWidth
                                        error={isError('advanced_days_invitable	')}
                                        type='number'
                                        InputProps={{
                                            inputProps: {
                                                min: 1,
                                                step: 1
                                            }
                                        }}
                                        disabled={! event.repeated}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Collapse>
            </Grid>
        </Grid>
    )
}
